/**
 * @fileoverview Vendor Hooks
 *
 * Custom React hooks for vendor management using React Query.
 * These hooks connect to Firebase Functions and provide caching,
 * loading states, and error handling for vendor operations.
 *
 * @version 1.0.0
 * @created 2025-12-11
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// =============================================================================
// READ HOOKS
// =============================================================================

/**
 * Hook to fetch all vendors with optional filtering
 *
 * @param {Object} options - Filter options
 * @param {string} options.status - Filter by status ("Active" or "Inactive")
 * @param {string} options.category - Filter by vendor category
 * @param {boolean} options.clientFacing - Filter by client-facing flag
 * @param {boolean} options.poolVendor - Filter by pool vendor flag
 * @returns {Object} Query result with vendors data
 */
export function useVendors(options = {}) {
  const { status, category, clientFacing, poolVendor } = options;

  return useQuery({
    queryKey: ["vendors", status, category, clientFacing, poolVendor],
    queryFn: async () => {
      const getVendors = httpsCallable(functions, "getAllVendors");
      // Only include defined options
      const params = {};
      if (status !== undefined) params.status = status;
      if (category !== undefined) params.category = category;
      if (clientFacing !== undefined) params.clientFacing = clientFacing;
      if (poolVendor !== undefined) params.poolVendor = poolVendor;
      const result = await getVendors({
        status,
        category,
        clientFacing,
        poolVendor,
      });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch vendors");
      }

      return result.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch active vendors only
 *
 * @returns {Object} Query result with active vendors
 */
export function useActiveVendors() {
  return useQuery({
    queryKey: ["vendors", "active"],
    queryFn: async () => {
      const getActiveVendors = httpsCallable(functions, "getActiveVendors");
      const result = await getActiveVendors();

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch active vendors");
      }

      return result.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });
}
/**
 * Hook to fetch a single vendor by ID
 *
 * @param {string} vendorId - Vendor record ID
 * @returns {Object} Query result with vendor data
 */
export function useVendor(vendorId) {
  return useQuery({
    queryKey: ["vendor", vendorId],
    queryFn: async () => {
      const getVendor = httpsCallable(functions, "getVendorById");
      const result = await getVendor({ vendorId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch vendor");
      }

      return result.data.data;
    },
    enabled: !!vendorId, // Only run if vendorId is provided
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to search vendors by name
 *
 * @param {string} searchTerm - Search term
 * @returns {Object} Query result with matching vendors
 */
export function useVendorSearch(searchTerm) {
  return useQuery({
    queryKey: ["vendorSearch", searchTerm],
    queryFn: async () => {
      const search = httpsCallable(functions, "searchVendors");
      const result = await search({ searchTerm });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to search vendors");
      }

      return result.data.data;
    },
    enabled: !!searchTerm && searchTerm.length >= 2, // Only run if 2+ characters
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch vendor categories
 *
 * @returns {Object} Query result with categories array
 */
export function useVendorCategories() {
  return useQuery({
    queryKey: ["vendorCategories"],
    queryFn: async () => {
      const getCategories = httpsCallable(functions, "getVendorCategories");
      const result = await getCategories();

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch categories");
      }

      return result.data.data;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes (categories change rarely)
  });
}

/**
 * Hook to fetch vendor statistics
 *
 * @returns {Object} Query result with stats data
 */
export function useVendorStats() {
  return useQuery({
    queryKey: ["vendorStats"],
    queryFn: async () => {
      const getStats = httpsCallable(functions, "getVendorStats");
      const result = await getStats();

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch vendor stats");
      }

      return result.data.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch tasks assigned to a vendor
 *
 * @param {string} vendorId - Vendor record ID
 * @param {string} status - Optional status filter
 * @returns {Object} Query result with tasks array
 */
export function useVendorTasks(vendorId, status = null) {
  return useQuery({
    queryKey: ["vendorTasks", vendorId, status],
    queryFn: async () => {
      const getTasks = httpsCallable(functions, "getVendorTasks");
      const result = await getTasks({ vendorId, status });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch vendor tasks");
      }

      return result.data.data;
    },
    enabled: !!vendorId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new vendor
 *
 * @returns {Object} Mutation object with mutate function
 */
export function useCreateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vendorData) => {
      const create = httpsCallable(functions, "createVendor");
      const result = await create(vendorData);

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to create vendor");
      }

      return result.data.data;
    },
    onSuccess: () => {
      // Invalidate vendors list to refetch
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({ queryKey: ["vendorStats"] });
    },
  });
}

/**
 * Hook to update a vendor
 *
 * @returns {Object} Mutation object with mutate function
 */
export function useUpdateVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ vendorId, updates }) => {
      const update = httpsCallable(functions, "updateVendor");
      const result = await update({ vendorId, updates });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to update vendor");
      }

      return result.data.data;
    },
    onSuccess: (data, variables) => {
      // Update the specific vendor in cache
      queryClient.setQueryData(["vendor", variables.vendorId], data);
      // Invalidate vendors list
      queryClient.invalidateQueries({ queryKey: ["vendors"] });
      queryClient.invalidateQueries({ queryKey: ["vendorStats"] });
    },
  });
}

// =============================================================================
// DEFAULT EXPORT
// =============================================================================

export default {
  useVendors,
  useActiveVendors,
  useVendor,
  useVendorSearch,
  useVendorCategories,
  useVendorStats,
  useVendorTasks,
  useCreateVendor,
  useUpdateVendor,
};
